import { Link, useNavigate, useRouterState } from "@tanstack/react-router";
import { type MessageKey, toggleLocale, useLocale, useT } from "@trust/i18n";
import { useState } from "react";
import { useAuthStore } from "@/lib/auth";

/**
 * 门户两段式顶栏(任务 0.2).
 * 复刻自旧站 fastj/client/src/views/inx/portal/components/header.vue:
 * - 第一行 80px #1d2b40:logo(仅桌面)+ 平台名(i18n)+ 语言切换 + 登录后"去注册标准"
 * - 第二行(仅桌面)80px 白底导航,6 项,选中 #1d2b40 底白字
 * - 移动端(<960px,Tailwind 断点 portal)☰ 打开左侧 200px drawer
 * 全部文案走 @trust/i18n,字体规格:标题 zh 28px / en 22px / 移动 14px;导航 zh 24px / en 20px,700。
 */

interface NavItem {
  path: string;
  labelKey: MessageKey;
}

const NAV_ITEMS: NavItem[] = [
  { path: "/", labelKey: "inx.header.home" },
  { path: "/inx/search", labelKey: "inx.header.standardsLibrary" },
  { path: "/inx/regInx", labelKey: "inx.header.registerStandard" },
  { path: "/inx/myInx", labelKey: "inx.header.myStandard" },
  { path: "/inx/rank", labelKey: "inx.header.standardRanking" },
  { path: "/inx/about", labelKey: "inx.header.aboutUs" },
];

function MenuIcon() {
  return (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M3 6h18M3 12h18M3 18h18"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}

function GlobeIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.6" />
      <path
        d="M3 12h18M12 3c2.5 2.4 3.8 5.6 3.8 9s-1.3 6.6-3.8 9c-2.5-2.4-3.8-5.6-3.8-9S9.5 5.4 12 3z"
        stroke="currentColor"
        strokeWidth="1.6"
      />
    </svg>
  );
}

export function PortalHeader() {
  const t = useT();
  const locale = useLocale();
  const loggedIn = useAuthStore((s) => s.loggedIn);
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  const isActive = (path: string) => pathname === path;
  // 字号规格:标题 zh 28 / en 22 / 移动 14;导航 zh 24 / en 20
  // 移动端标题允许收缩省略(min-w-0 + truncate),避免与右侧语言/用户区重叠
  const titleClass = `shrink-0 overflow-hidden font-bold text-white text-[14px] max-portal:min-w-0 max-portal:shrink max-portal:truncate ${
    locale === "en" ? "portal:text-[22px]" : "portal:text-[28px]"
  }`;
  const navTextClass = locale === "en" ? "text-[20px]" : "text-2xl";

  return (
    // fixed 常驻顶部:桌面 160px(两行)、移动 80px(仅第一行),由 PortalLayout 补对应 padding-top
    <header className="fixed top-0 right-0 left-0 z-40">
      {/* 第一行:藏青 80px(移动端收窄左右留白) */}
      <div className="flex h-20 items-center justify-between bg-portal-dark px-10 max-portal:px-4">
        <div className="flex items-center max-portal:min-w-0 max-portal:flex-1">
          {/* 移动端 ☰(旧站 svg-icon menu)*/}
          <button
            type="button"
            onClick={() => setDrawerOpen(true)}
            aria-label="menu"
            className="mr-2 cursor-pointer border-0 bg-transparent p-0 text-white portal:hidden"
          >
            <MenuIcon />
          </button>
          <Link to="/" className="flex items-center no-underline max-portal:min-w-0">
            {/* logo 仅桌面 */}
            <img src="/portal/logo1.png" alt="TRUST" className="h-10 shrink-0 max-portal:hidden" />
            <span className={`ml-5 ${titleClass}`}>{t("inx.title")}</span>
          </Link>
        </div>
        <div className="flex items-center max-portal:shrink-0">
          {/* 语言切换(点击切到另一种语言)*/}
          <button
            type="button"
            onClick={toggleLocale}
            aria-label="switch language"
            className="flex cursor-pointer items-center gap-1 border-0 bg-transparent p-0 text-white"
          >
            <GlobeIcon />
            <span className="text-sm">{locale === "zh" ? "EN" : "中文"}</span>
          </button>
          {/* 登录后显示"去注册标准"(仅桌面)→ 管理台新建指南 */}
          {loggedIn && (
            <Link to="/admin/guides/new" className="ml-5 text-white no-underline max-portal:hidden">
              {t("inx.toRegister")}
            </Link>
          )}
          {/* 用户态:登录后用户名下拉(管理台入口对所有登录用户开放,台内菜单按角色过滤)+ 退出登录;未登录显示登录/注册 */}
          {loggedIn ? (
            <div
              className="relative ml-5"
              onMouseEnter={() => setUserMenuOpen(true)}
              onMouseLeave={() => setUserMenuOpen(false)}
            >
              <button
                type="button"
                onClick={() => setUserMenuOpen((v) => !v)}
                className="flex cursor-pointer items-center gap-1 border-0 bg-transparent p-0 text-white"
              >
                <span className="max-w-[120px] overflow-hidden text-ellipsis whitespace-nowrap text-sm max-portal:max-w-[64px]">
                  {user?.real_name || user?.username}
                </span>
                <span className="text-[10px]">▾</span>
              </button>
              {userMenuOpen && (
                <div className="absolute right-0 top-full z-50 w-36 overflow-hidden rounded bg-white py-1 shadow-lg">
                  <Link
                    to="/admin"
                    onClick={() => setUserMenuOpen(false)}
                    className="block px-4 py-2 text-sm text-[#333333] no-underline hover:bg-gray-100"
                  >
                    {t("app.adminConsole")}
                  </Link>
                  <button
                    type="button"
                    onClick={() => {
                      setUserMenuOpen(false);
                      logout();
                      navigate({ to: "/" });
                    }}
                    className="block w-full cursor-pointer border-0 bg-transparent px-4 py-2 text-left text-sm text-[#333333] hover:bg-gray-100"
                  >
                    {t("app.logout")}
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="ml-5 flex items-center gap-3 text-sm">
              <Link to="/inx/login" className="text-white no-underline">
                {t("app.login")}
              </Link>
              <span className="text-white/50">/</span>
              <Link to="/inx/register" className="text-white no-underline">
                {t("app.register")}
              </Link>
            </div>
          )}
        </div>
      </div>

      {/* 第二行:白底导航 80px(仅桌面)*/}
      <nav className="flex h-20 justify-center bg-white max-portal:hidden">
        <div className="flex h-20 w-4/5 items-center justify-around px-10">
          {NAV_ITEMS.map((item) => {
            const active = isActive(item.path);
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`mr-10 flex h-15 shrink-0 items-center justify-center px-2.5 font-bold no-underline ${navTextClass} ${
                  active ? "bg-portal-dark text-white" : "text-[#333333]"
                }`}
              >
                {t(item.labelKey)}
              </Link>
            );
          })}
        </div>
      </nav>

      {/* 移动端 drawer:左侧 200px(遮罩 + 面板)*/}
      {drawerOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/40 portal:hidden"
          onClick={() => setDrawerOpen(false)}
          onKeyDown={(e) => e.key === "Escape" && setDrawerOpen(false)}
          role="presentation"
        />
      )}
      <aside
        className={`fixed top-0 left-0 z-50 h-full w-[200px] bg-white transition-transform duration-200 portal:hidden ${
          drawerOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="px-2.5 pt-2.5">
          {NAV_ITEMS.map((item) => {
            const active = isActive(item.path);
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setDrawerOpen(false)}
                className={`mb-2.5 flex h-15 shrink-0 items-center justify-center px-2.5 text-2xl font-bold no-underline ${
                  active ? "bg-portal-dark text-white" : "text-[#333333]"
                }`}
              >
                {t(item.labelKey)}
              </Link>
            );
          })}
        </div>
      </aside>
    </header>
  );
}
